/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-side-effects, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Scopes from '@linc-technologies/linced-common/enums/scopes';

export default class PostSurvey extends Component {
  // Component versioning
  @tracked visualStyle = 'v1';

  // Services
  @service
  permissions;

  @service
  session;

  @service
  store;

  Scopes = Scopes;
  groups = null;

  // `@groups` is `post.groups`, which aliases an async hasMany — so it arrives
  // as a PromiseManyArray. `isEmpty`/`isArray` on one trips ember-data's
  // "Do not use A() on an EmberData PromiseManyArray", and iterating it is
  // deprecated too. Read `content` once it has resolved; the computed
  // re-runs when it does.
  @computed('groups.{content,isFulfilled}')
  get resolvedGroups() {
    let groups = this.groups;
    if (!groups) {
      return [];
    }
    // A PromiseManyArray exposes its ManyArray as `content` once fulfilled.
    return groups.content ?? groups;
  }

  @computed('session.scopes.[]', 'resolvedGroups.@each.learners')
  get learners() {
    if (this.permissions.equal(Scopes.LEARNER)) {
      // User is a student.  Just return their own Person record.
      return [this.get('session.person')];
    }
    if (isEmpty(this.resolvedGroups)) {
      return [];
    }
    return this.resolvedGroups.reduce((learners, group) => {
      if (isEmpty(group.learners)) {
        return learners;
      }
      group.learners.forEach((member) => {
        if (member.status === 1) {
          // Learner is a current member of this group.
          let learner = member.get('person');
          // If `learner` is empty, skip to prevent errors.
          if (!isEmpty(learner) && !learners.includes(learner)) {
            learners.push(learner);
          }
        }
      });
      return learners;
    }, []);
  }

  @tracked numChanges = 0; // Increment to trigger a reload of the responses.

  // responses holds the settled, plain-array result of the query fired by
  // responsesQuery below.
  @tracked responses = [];
  responsesRequestId = 0;

  // store.query() now returns a bare Promise rather than a PromiseArray, so
  // there's no synchronous .content/.isFulfilled proxy left to point a
  // computed's dependent keys at directly. This computed exists to keep
  // firing (and re-firing) that query reactively - 'learners.@each.id'
  // chain-watches through resolvedGroups down to groups.{content,isFulfilled}
  // resolving, same as before - and stashes the resolved records into the
  // `responses` tracked field. isFullyCompleted lists it as a dependent key
  // purely so it stays read/chain-watched; nothing consumes its return value.
  @computed('learners.@each.id', 'numChanges')
  get responsesQuery() {
    let rawIds = this.learners.reduce((ids, learner) => {
      if (!isEmpty(learner) && !isEmpty(learner.get('id'))) {
        ids.push(learner.get('id'));
      }
      return ids;
    }, []);
    let personIds = JSON.stringify(rawIds);
    let surveyId = this.get('survey.id');
    // incNumChanges can fire a new query before a prior one has settled (a
    // save while one is still in flight); only the most recent request's
    // result should ever land, or a slower, now-stale response can overwrite
    // it after the fact.
    let requestId = ++this.responsesRequestId;
    this.store
      .query('surveys/response', { surveyId, personIds })
      .then((records) => {
        if (requestId === this.responsesRequestId) {
          this.responses = records.slice();
        }
      });
    return personIds;
  }

  // isFullyCompleted checks if there are responses for all children involved
  // in the survey
  @computed('learners.[]', 'responses.[]', 'responsesQuery')
  get isFullyCompleted() {
    let learnersIds = this.learners.map((learner) => learner.personId);
    let responseIds = this.responses.map((response) => response.person_id);

    return learnersIds.every((learnerId) => responseIds.includes(learnerId));
  }

  @action
  incNumChanges() {
    this.incrementProperty('numChanges');
  }
}
